import apiClient from './client';
import type { ApiResponse } from './types';

const AUDIT_BASE = '/api/audit';

/**
 * Operation log entry
 */
export interface OperationLogEntry {
  id: number;
  memberId: number | null;
  memberName: string | null;
  memberEmail: string | null;
  operationType: number | null;
  operationTypeName: string | null;
  operationReference: number | null;
  message: string | null;
  operationTime: string | null;
}

/**
 * Paginated response for operation logs
 */
export interface OperationLogPagedResponse {
  items: OperationLogEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Operation type info
 */
export interface OperationTypeInfo {
  code: number;
  name: string;
}

/**
 * Query parameters for fetching logs
 */
export interface GetLogsParams {
  memberId?: number;
  operationType?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Get operation logs with optional filtering and pagination (admin only)
 */
export async function getOperationLogs(
  params: GetLogsParams = {}
): Promise<ApiResponse<OperationLogPagedResponse>> {
  const queryParams = new URLSearchParams();
  
  if (params.memberId !== undefined) {
    queryParams.append('memberId', params.memberId.toString());
  }
  if (params.operationType !== undefined) {
    queryParams.append('operationType', params.operationType.toString());
  }
  if (params.startDate) {
    queryParams.append('startDate', params.startDate);
  }
  if (params.endDate) {
    queryParams.append('endDate', params.endDate);
  }
  if (params.page !== undefined) {
    queryParams.append('page', params.page.toString());
  }
  if (params.pageSize !== undefined) {
    queryParams.append('pageSize', params.pageSize.toString());
  }
  
  const queryString = queryParams.toString();
  const url = `${AUDIT_BASE}/logs${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiClient.get<ApiResponse<OperationLogPagedResponse>>(url);
  return response.data;
}

/**
 * Get available operation types (admin only)
 */
export async function getOperationTypes(): Promise<ApiResponse<OperationTypeInfo[]>> {
  const response = await apiClient.get<ApiResponse<OperationTypeInfo[]>>(
    `${AUDIT_BASE}/operation-types`
  );
  return response.data;
}

export const auditApi = {
  getOperationLogs,
  getOperationTypes,
};
